import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Prompts the user for how many servings of a scanned product they actually ate,
 * then confirms the total calories to log. Handles per-serving and per-100g data.
 */
const ServingSizeDialog = ({ open, product, onCancel, onConfirm, submitting }) => {
  const [servings, setServings] = useState(1);
  const [grams, setGrams] = useState(0);

  const perServing = product?.calories_per_serving;
  const per100g = product?.calories_per_100g;
  // Mode: use serving units if we have them, else grams
  const mode = perServing != null ? 'serving' : per100g != null ? 'grams' : 'serving';

  useEffect(() => {
    if (!open) return;
    setServings(1);
    setGrams(product?.serving_grams ? Math.round(product.serving_grams) : 100);
  }, [open, product]);

  const totalCalories = useMemo(() => {
    if (!product) return 0;
    if (mode === 'serving' && perServing != null) return Math.max(0, Math.round(perServing * servings));
    if (mode === 'grams' && per100g != null) return Math.max(0, Math.round((per100g * grams) / 100));
    return product.calories || 0;
  }, [product, mode, servings, grams, perServing, per100g]);

  if (!open || !product) return null;

  const step = (delta) => {
    if (mode === 'serving') setServings((s) => Math.max(0.5, Math.round((s + delta) * 2) / 2));
    else setGrams((g) => Math.max(5, g + delta));
  };

  const confirm = () => {
    onConfirm({
      name: product.name,
      calories: totalCalories,
      meta: {
        code: product.code,
        brand: product.brand,
        servings: mode === 'serving' ? servings : undefined,
        grams: mode === 'grams' ? grams : undefined,
        calories_per_serving: perServing,
        calories_per_100g: per100g,
        serving: product.serving,
      },
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="serving-size-dialog"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          className="w-full max-w-md bg-[#FAF9F6] rounded-3xl overflow-hidden shadow-2xl"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1D8]">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">
              How much did you eat?
            </span>
            <button
              onClick={onCancel}
              className="w-8 h-8 rounded-full hover:bg-[#F2EFE9] flex items-center justify-center text-[#4A5A53]"
              data-testid="serving-size-close"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          <div className="px-5 py-5 space-y-5">
            <div className="flex gap-3 items-center">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-16 h-16 rounded-2xl object-cover border border-[#E5E1D8] bg-white"
                />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-[#F2EFE9] flex items-center justify-center text-2xl">🍎</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-serif-display text-lg text-[#1A2421] leading-tight truncate" data-testid="serving-size-name">
                  {product.name}
                </div>
                {product.brand && (
                  <div className="text-xs text-[#4A5A53] truncate">{product.brand}</div>
                )}
                <div className="text-xs text-[#4A5A53] mt-1">
                  {perServing != null && (
                    <>{perServing} cal / serving{product.serving ? ` (${product.serving})` : ''}</>
                  )}
                  {perServing == null && per100g != null && <>{per100g} cal / 100 g</>}
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E1D8] p-4">
              <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#4A5A53] mb-3">
                {mode === 'serving' ? 'Servings eaten' : 'Grams eaten'}
              </div>
              <div className="flex items-center justify-between">
                <Button
                  onClick={() => step(mode === 'serving' ? -0.5 : -10)}
                  variant="outline"
                  size="icon"
                  className="rounded-full border-[#E5E1D8] w-12 h-12"
                  data-testid="serving-size-decrement"
                >
                  <Minus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
                <div className="text-center">
                  <div className="font-serif-display text-4xl text-[#1A2421] tabular-nums" data-testid="serving-size-count">
                    {mode === 'serving' ? servings : grams}
                  </div>
                  <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#4A5A53] mt-1">
                    {mode === 'serving' ? (servings === 1 ? 'serving' : 'servings') : 'grams'}
                  </div>
                </div>
                <Button
                  onClick={() => step(mode === 'serving' ? 0.5 : 10)}
                  variant="outline"
                  size="icon"
                  className="rounded-full border-[#E5E1D8] w-12 h-12"
                  data-testid="serving-size-increment"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            <div className="flex items-baseline justify-between px-1">
              <span className="text-sm text-[#4A5A53] font-light">Total to log</span>
              <span className="font-serif-display text-3xl text-[#1A2421] tabular-nums" data-testid="serving-size-total">
                {totalCalories} <span className="text-sm font-sans text-[#4A5A53]">cal</span>
              </span>
            </div>
          </div>

          <div className="px-5 py-4 border-t border-[#E5E1D8] flex gap-2 justify-end">
            <Button
              onClick={onCancel}
              variant="outline"
              className="rounded-full border-[#E5E1D8]"
              data-testid="serving-size-cancel"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirm}
              className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-6 gap-2"
              data-testid="serving-size-confirm"
              disabled={submitting || totalCalories <= 0}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Log ${totalCalories} cal`}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ServingSizeDialog;
